import { Box, Typography } from '@mui/material';
import Tooltip from 'components/Tooltip';
import Icon from 'components/Icon';
import { useAppSelector } from 'store/hooks';
import {
  selectAggregateMOSMedianReceiving,
  selectIndicatorAverageMOS,
  selectPublishingMOSWorst,
  selectPublishingVideoMOS,
  selectReceivingMOSMedian,
} from 'features/system-stats/systemStatsSlice';

const formatMOS = (value: number | null): string => (value === null ? '—' : value.toFixed(2));

const TOOLTIPS = {
  indicatorSmoothed:
    '5-sample rolling average of the live aggregate. This is what the indicator dot reads; smoothing dampens per-tick jitter.',
  worstPublishing:
    'Worst best-deliverable layer across your published sources (camera/screenshare). Drives the weak-connection toast (broadcasters only) via its own rolling average.',
  publishingVideo: 'MOS of your outgoing video stream.',
  medianReceiving:
    'Median MOS across incoming tracks. Filters single-publisher outliers; feeds the live aggregator.',
  liveAggregate:
    'Live indicator source: min(worstPublishing, median(receiving)). worstPublishing = worst best-deliverable layer per published source.',
} as const;

type FieldProps = {
  label: string;
  value: number | null;
  tooltip: string;
};

const Field = ({ label, value, tooltip }: FieldProps) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption">{label}</Typography>
      <Tooltip placement="top" title={tooltip}>
        <Icon
          name="question"
          aria-label={`${label} info`}
          sx={{
            fontSize: 14,
            color: 'text.secondary',
            cursor: 'help',
          }}
        />
      </Tooltip>
    </Box>
    <Typography>{formatMOS(value)}</Typography>
  </Box>
);

const AggregatorPanel = () => {
  const indicatorAverage = useAppSelector(selectIndicatorAverageMOS);
  const publishing = useAppSelector(selectPublishingMOSWorst);
  const publishingVideo = useAppSelector(selectPublishingVideoMOS);
  const receivingMedian = useAppSelector(selectReceivingMOSMedian);
  const aggregateMedianReceiving = useAppSelector(selectAggregateMOSMedianReceiving);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 1, mb: 2 }}>
      <Field
        label="Indicator (smoothed)"
        value={indicatorAverage}
        tooltip={TOOLTIPS.indicatorSmoothed}
      />
      <Field label="Worst publishing" value={publishing} tooltip={TOOLTIPS.worstPublishing} />
      <Field label="Publishing video" value={publishingVideo} tooltip={TOOLTIPS.publishingVideo} />
      <Field label="Median receiving" value={receivingMedian} tooltip={TOOLTIPS.medianReceiving} />
      <Field
        label="Live aggregate"
        value={aggregateMedianReceiving}
        tooltip={TOOLTIPS.liveAggregate}
      />
    </Box>
  );
};

export default AggregatorPanel;
